import { type MutableRefObject } from 'react';

import { Button, Input, Select, Text, Textarea, type TypeSelectOption } from '@thefarmersfront/kitchen';

import { FormField, ModalSection } from '~/components/common';

import { useKakaoPostcodeSearch } from '~/hooks/common';
import { useClaimStore } from '~/store';
import { validateNumberAndHyphenOnly } from '~/utils';

import { DEFAULT_INPUT_STATUS } from '~/constants';

import type { TypeReturnRegisterSecondStepValues, TypeReturnRegisterSecondStepValuesState } from '~/types/claim.types';

import styles from '../ClaimReturnRegisterSecondStepContent.module.scss';

const ClaimReturnRegisterSecondStepReturnInfo = ({
  data,
  dataState,
  onChange,
  onChangeState,
  errorFieldRefs,
}: {
  data: TypeReturnRegisterSecondStepValues;
  dataState: TypeReturnRegisterSecondStepValuesState;
  onChange: (values: Partial<TypeReturnRegisterSecondStepValues>) => void;
  onChangeState: (values: Partial<TypeReturnRegisterSecondStepValuesState>) => void;
  errorFieldRefs: MutableRefObject<Record<string, HTMLElement | null>>;
}) => {
  const { handleZipCodeSearchOpen } = useKakaoPostcodeSearch({
    onComplete: (_, address) => onChange({ primaryAddress: address }),
  });

  const selectOptions = useClaimStore((state) => state.selectOptions);

  const options = {
    returnPlace: selectOptions.returnPlace,
    accessMethod: selectOptions.accessMethod,
  };

  return (
    <ModalSection className={styles['return-info']} title="회수 정보">
      <FormField
        label="원주문 수령자명"
        required
        contentAlign="center"
        contents={<Text size="large">{data.receiverName}</Text>}
      />
      <FormField
        label="연락처"
        required
        contents={
          <Input
            ref={(el) => {
              errorFieldRefs.current.receiverContactNumber = el;
            }}
            type="text"
            placeholder="- 포함 입력"
            size="small"
            maxWidth="148px"
            status={dataState.receiverContactNumber.status}
            validationMessage={dataState.receiverContactNumber.message}
            value={data.receiverContactNumber}
            onChange={(e) => {
              const newValue = e.target.value;
              if (validateNumberAndHyphenOnly(newValue)) {
                onChange({ receiverContactNumber: newValue });
                onChangeState({ receiverContactNumber: DEFAULT_INPUT_STATUS });
              }
            }}
            onBlur={(e) => onChange({ receiverContactNumber: e.target.value.trim() })}
            onClick={() => onChangeState({ receiverContactNumber: DEFAULT_INPUT_STATUS })}
          />
        }
      />
      <FormField
        label="기본주소"
        required
        contents={
          <>
            <Input
              ref={(el) => {
                errorFieldRefs.current.primaryAddress = el;
              }}
              type="text"
              size="small"
              disabled
              status={dataState.primaryAddress.status}
              validationMessage={dataState.primaryAddress.message}
              value={data.primaryAddress}
            />
            <Button
              size="small"
              color="secondary"
              onClick={() => {
                handleZipCodeSearchOpen();
                onChangeState({ primaryAddress: DEFAULT_INPUT_STATUS });
              }}
            >
              검색
            </Button>
          </>
        }
        contentsClassName={styles['address-container']}
      />
      <FormField
        label="상세주소"
        contents={
          <Input
            type="text"
            placeholder="입력"
            size="small"
            maxLength={30}
            count
            value={data.detailAddress}
            onChange={(e) => onChange({ detailAddress: e.target.value })}
            onBlur={(e) => onChange({ detailAddress: e.target.value.trim() })}
          />
        }
      />
      <FormField
        label="회수장소"
        required
        contents={
          <Select
            ref={(el) => {
              errorFieldRefs.current.returnPlace = el;
            }}
            placeholder="선택"
            size="small"
            maxWidth="148px"
            options={options.returnPlace}
            status={dataState.returnPlace.status}
            validationMessage={dataState.returnPlace.message}
            value={data.returnPlace}
            onChange={(_: unknown, value: TypeSelectOption<string>) => onChange({ returnPlace: value })}
            onClick={() => onChangeState({ returnPlace: DEFAULT_INPUT_STATUS })}
          />
        }
      />
      <FormField
        label="회수장소 상세"
        contents={
          <Textarea
            placeholder="입력"
            size="small"
            count
            maxLength={30}
            value={data.returnPlaceDetail}
            onChange={(e) => onChange({ returnPlaceDetail: e.target.value })}
            onBlur={(e) => onChange({ returnPlaceDetail: e.target.value.trim() })}
          />
        }
      />
      <FormField
        label="출입방법"
        required
        contents={
          <Select
            ref={(el) => {
              errorFieldRefs.current.accessMethod = el;
            }}
            placeholder="선택"
            size="small"
            maxWidth="148px"
            options={options.accessMethod}
            status={dataState.accessMethod.status}
            validationMessage={dataState.accessMethod.message}
            value={data.accessMethod}
            onChange={(_: unknown, value: TypeSelectOption<string>) => onChange({ accessMethod: value })}
            onClick={() => onChangeState({ accessMethod: DEFAULT_INPUT_STATUS })}
          />
        }
      />
      <FormField
        label="출입방법 상세"
        contents={
          <Textarea
            placeholder="입력"
            size="small"
            count
            maxLength={30}
            value={data.accessDetail}
            onChange={(e) => onChange({ accessDetail: e.target.value })}
            onBlur={(e) => onChange({ accessDetail: e.target.value.trim() })}
          />
        }
      />
    </ModalSection>
  );
};

export default ClaimReturnRegisterSecondStepReturnInfo;
